import React, { useEffect, useState } from "react";
import { Building2, Plus, Pencil, Users, Package, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, arabicError } from "../lib/api";
import LocationPicker from "../components/LocationPicker";

const inputCls = "w-full h-11 px-3 rounded-lg border border-[#7AB2B2] focus:border-[#09637E] focus:ring-2 focus:ring-[#09637E]/20 outline-none text-sm";
const empty = { name: "", address: "", city: "", area: "", phone: "", location: null };

const BranchForm = ({ initial, isMain, onSubmit, onCancel, saving }) => {
  const [f, setF] = useState({ ...empty, ...initial });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!isMain && !f.name.trim()) { toast.error("اسم الفرع مطلوب"); return; } onSubmit(f); }} className="space-y-2" data-testid="branch-form">
      {!isMain && <input placeholder="اسم الفرع *" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="branch-name" />}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input placeholder="المدينة / المحافظة" value={f.city || ""} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid="branch-city" />
        <input placeholder="المنطقة" value={f.area || ""} onChange={(e) => set("area", e.target.value)} className={inputCls} data-testid="branch-area" />
        <input placeholder="العنوان التفصيلي" value={f.address || ""} onChange={(e) => set("address", e.target.value)} className={`${inputCls} sm:col-span-2`} data-testid="branch-address" />
        <input type="tel" placeholder="هاتف الفرع" value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="branch-phone" dir="ltr" />
      </div>
      <LocationPicker value={f.location} onChange={(loc) => set("location", loc)} testidPrefix="branch-loc" />
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="h-11 px-5 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold disabled:opacity-60" data-testid="branch-save">{saving ? "جارٍ الحفظ..." : "حفظ"}</button>
        <button type="button" onClick={onCancel} className="h-11 px-4 rounded-lg border border-[#7AB2B2] text-[#333333] font-bold" data-testid="branch-cancel">إلغاء</button>
      </div>
    </form>
  );
};

const BranchCard = ({ b, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async (f) => {
    setSaving(true);
    try {
      const body = { address: f.address || "", city: f.city || "", area: f.area || "", phone: f.phone || "", location: f.location || null, clear_location: !f.location };
      if (!b.is_main) body.name = f.name.trim();
      await api.patch(`/branches/${b.id}`, body); toast.success("تم تحديث الفرع"); setEdit(false); onSaved();
    } catch (e) { toast.error(arabicError(e)); } finally { setSaving(false); }
  };
  const where = [b.city, b.area, b.address].filter(Boolean).join(" · ");
  return (
    <div className={`bg-white border rounded-xl p-4 space-y-3 ${b.is_main ? "border-[#09637E]" : "border-[#7AB2B2]"}`} data-testid={`branch-card-${b.id}`} data-branch-main={b.is_main ? "1" : "0"}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Building2 size={18} className="text-[#09637E] shrink-0" />
            <span className="font-extrabold text-[#333333]" data-testid={`branch-name-${b.id}`}>{b.name}</span>
            {b.is_main && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#EBF4F6] text-[#09637E]">الفرع الافتراضي</span>}
          </div>
          <div className="text-xs text-[#666666] mt-1 flex items-center gap-1 break-words"><MapPin size={12} className="shrink-0" /> {where || "لم يُحدَّد العنوان بعد"}</div>
          {b.phone && <div className="text-xs text-[#666666] flex items-center gap-1" dir="ltr"><Phone size={12} /> {b.phone}</div>}
        </div>
        <button onClick={() => setEdit((v) => !v)} className="h-10 px-3 rounded-lg border-2 border-[#088395] text-[#088395] text-sm font-bold flex items-center gap-1 hover:bg-[#EBF4F6]" data-testid={`branch-edit-${b.id}`}><Pencil size={14} /> تعديل</button>
      </div>
      <div className="flex flex-wrap gap-3 text-xs font-bold text-[#333333]">
        <span className="flex items-center gap-1 bg-[#F7FBFC] px-2 py-1 rounded" data-testid={`branch-emp-count-${b.id}`}><Users size={13} /> {b.employee_count} موظف</span>
        <span className="flex items-center gap-1 bg-[#F7FBFC] px-2 py-1 rounded" data-testid={`branch-prod-count-${b.id}`}><Package size={13} /> {b.product_count} منتج</span>
      </div>
      {b.employees?.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`branch-employees-${b.id}`}>
          {b.employees.map((e) => (
            <span key={e.id} className={`text-[11px] px-2 py-0.5 rounded-full border ${e.designation === "branch_manager" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-white border-[#DCEBEE] text-[#333333]"} ${e.is_active ? "" : "opacity-50 line-through"}`}>
              {e.name} — {e.designation_ar}
            </span>
          ))}
        </div>
      )}
      {edit && <BranchForm initial={b} isMain={b.is_main} saving={saving} onSubmit={save} onCancel={() => setEdit(false)} />}
    </div>
  );
};

export default function Branches() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/branches").then((r) => setItems(r.data)).catch((e) => toast.error(arabicError(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const create = async (f) => {
    setSaving(true);
    try {
      await api.post("/branches", { name: f.name.trim(), address: f.address, city: f.city, area: f.area, phone: f.phone, location: f.location || null });
      toast.success("تم إنشاء الفرع"); setAdding(false); load();
    } catch (e) { toast.error(arabicError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-6" data-testid="branches-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#333333] flex items-center gap-2"><Building2 className="text-[#09637E]" /> الفروع</h1>
          <p className="text-sm text-[#666666] mt-1">الفرع الرئيسي يمثّل متجرك الحالي. يمكنك إضافة فروع أخرى وتعيين الموظفين إليها من صفحة الموظفين.</p>
        </div>
        <button onClick={() => setAdding((v) => !v)} className="h-11 px-4 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold flex items-center gap-2" data-testid="branch-add-btn"><Plus size={18} /> فرع جديد</button>
      </div>
      {adding && <div className="bg-white border border-[#7AB2B2] rounded-xl p-4" data-testid="branch-create-form"><BranchForm initial={empty} isMain={false} saving={saving} onSubmit={create} onCancel={() => setAdding(false)} /></div>}
      {loading ? <div className="text-center text-[#666666] py-8">جارٍ التحميل...</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="branches-list">
          {items.map((b) => <BranchCard key={b.id} b={b} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}
